import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OptimizedImage } from "@/components/OptimizedImage";
import { scooterApi, formatPrice, getFileUrl, SCOOTER_TYPES, DAMAGE_HISTORY } from "@/lib/api";
import { toast } from "sonner";
import { 
  Gauge,
  Calendar,
  Fuel,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Send,
  CheckCircle,
  Filter,
  X,
  Search
} from "lucide-react";

// Contact info - centralized
const CONTACT_INFO = {
  phone: "0792250058",
  whatsapp: "40792250058",
  email: "office@scutersh.com",
  location: "București, România"
};

// Generate WhatsApp link for a specific scooter
const generateWhatsAppLink = (scooter = null) => {
  let message;
  if (scooter) {
    const scooterUrl = `${window.location.origin}/scooter/${scooter.scooter_id}`;
    message = `Bună! Sunt interesat de scuterul ${scooter.brand} ${scooter.model} ${scooter.year} (${formatPrice(scooter.selling_price)}).

🔗 Link: ${scooterUrl}

Aș dori mai multe informații. Mulțumesc!`;
  } else {
    message = `Bună! Sunt interesat de scuterele voastre.

Aș dori mai multe informații. Mulțumesc!`;
  }
  
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodedMessage}`;
};

export default function ShowroomPage() {
  const [scooters, setScooters] = useState([]);
  const [filteredScooters, setFilteredScooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    scooter_type: "all",
    damage_history: "all",
    brand: "all",
    search: ""
  });
  const [showFilters, setShowFilters] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    scooterInterest: ""
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Apply filters when scooters or filters change
  useEffect(() => {
    applyFilters();
  }, [scooters, filters]);

  const fetchData = async () => {
    try {
      const [scootersData, optionsData] = await Promise.all([
        scooterApi.getShowroom(),
        scooterApi.getFilterOptions()
      ]);
      setScooters(scootersData);
      setFilteredScooters(scootersData);
      setFilterOptions(optionsData);
    } catch (error) {
      console.error("Failed to load showroom", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...scooters];

    // Filter by scooter type
    if (filters.scooter_type && filters.scooter_type !== "all") {
      result = result.filter(s => s.scooter_type === filters.scooter_type);
    }

    // Filter by damage history
    if (filters.damage_history && filters.damage_history !== "all") {
      result = result.filter(s => s.damage_history === filters.damage_history);
    }

    // Filter by brand
    if (filters.brand && filters.brand !== "all") {
      result = result.filter(s => s.brand.toLowerCase() === filters.brand.toLowerCase());
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(s => 
        s.brand.toLowerCase().includes(searchLower) ||
        s.model.toLowerCase().includes(searchLower)
      );
    }

    setFilteredScooters(result);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      scooter_type: "all",
      damage_history: "all",
      brand: "all",
      search: ""
    });
  };

  const activeFiltersCount = Object.entries(filters).filter(
    ([key, value]) => value && value !== "all" && value !== ""
  ).length;

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const message = `📬 Nouă cerere de la vitrina web!

👤 Nume: ${formData.name}
📧 Email: ${formData.email}
📞 Telefon: ${formData.phone}
${formData.scooterInterest ? `🛵 Scuter de interes: ${formData.scooterInterest}` : ''}

💬 Mesaj:
${formData.message}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    
    setFormSubmitted(true);
    setSubmitting(false);
    toast.success("Formularul a fost trimis pe WhatsApp!");
  };

  const defaultImage = "https://images.unsplash.com/photo-1608551883326-2c9bd5d52618?w=600&h=400&fit=crop";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary font-mono">Se încarcă...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="showroom-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 py-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <span className="font-heading font-black text-white text-xl">S</span>
            </div>
          </div>
          <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
            SCUTERSH
          </h1>
          <p className="text-muted-foreground font-mono text-sm mt-1">
            Scutere & Piese Premium
          </p>
          <div className="flex justify-center gap-4 mt-3">
            <Link to="/parts-showroom">
              <Button variant="outline" size="sm">
                Pièces détachées
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-6">
          <h2 className="font-heading font-bold text-xl md:text-2xl mb-2">
            Scootere Disponibile
          </h2>
          <p className="text-muted-foreground">
            Descoperiți selecția noastră de scootere premium second-hand
          </p>
        </div>

        {/* Quick Contact Bar */}
        <div className="flex flex-wrap justify-center gap-4 mb-6 p-4 bg-secondary/30 rounded-lg">
          <a 
            href={`tel:${CONTACT_INFO.phone}`}
            className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
          >
            <Phone className="w-4 h-4 text-primary" />
            <span className="font-mono">{CONTACT_INFO.phone}</span>
          </a>
          <a 
            href={`mailto:${CONTACT_INFO.email}`}
            className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
          >
            <Mail className="w-4 h-4 text-primary" />
            <span>{CONTACT_INFO.email}</span>
          </a>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{CONTACT_INFO.location}</span>
          </div>
          <Button
            size="sm"
            className="bg-green-500 hover:bg-green-600 text-white"
            onClick={() => window.open(generateWhatsAppLink(), '_blank')}
            data-testid="btn-whatsapp-general"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
        </div>

        {/* Filters Section */}
        <div className="mb-6 space-y-4">
          {/* Search and Filter Toggle */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Caută scuter (marcă, model)..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="pl-10"
                data-testid="search-scooters"
              />
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="relative"
              data-testid="btn-toggle-filters"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtre
              {activeFiltersCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-black rounded-full text-xs flex items-center justify-center font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <Card className="border-primary/30 animate-fade-in" data-testid="filter-panel">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-sm">Filtrează scuterele</h3>
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Șterge filtre
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Scooter Type Filter */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Tip Scuter</Label>
                    <Select
                      value={filters.scooter_type}
                      onValueChange={(v) => handleFilterChange("scooter_type", v)}
                    >
                      <SelectTrigger data-testid="filter-type">
                        <SelectValue placeholder="Toate tipurile" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toate tipurile</SelectItem>
                        {Object.entries(SCOOTER_TYPES).map(([key, { label, icon }]) => (
                          <SelectItem key={key} value={key}>
                            {icon} {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Damage History Filter */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Istoric Daune</Label>
                    <Select
                      value={filters.damage_history}
                      onValueChange={(v) => handleFilterChange("damage_history", v)}
                    >
                      <SelectTrigger data-testid="filter-damage">
                        <SelectValue placeholder="Toate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toate</SelectItem>
                        {Object.entries(DAMAGE_HISTORY).map(([key, { label, icon }]) => (
                          <SelectItem key={key} value={key}>
                            {icon} {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Brand Filter */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Marcă</Label>
                    <Select
                      value={filters.brand}
                      onValueChange={(v) => handleFilterChange("brand", v)}
                    >
                      <SelectTrigger data-testid="filter-brand">
                        <SelectValue placeholder="Toate mărcile" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toate mărcile</SelectItem>
                        {filterOptions?.brands?.map((brand) => (
                          <SelectItem key={brand} value={brand}>
                            {brand}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Scooter Count */}
        <div className="flex justify-center mb-6">
          <div className="bg-secondary/50 rounded-full px-6 py-2">
            <span className="font-mono text-primary font-bold">{filteredScooters.length}</span>
            <span className="text-muted-foreground ml-2">
              {filteredScooters.length === 1 ? "scuter disponibil" : "scootere disponibile"}
            </span>
            {activeFiltersCount > 0 && (
              <span className="text-muted-foreground ml-1">
                (din {scooters.length})
              </span>
            )}
          </div>
        </div>

        {/* Empty State */}
        {filteredScooters.length === 0 && (
          <Card className="border-border border-dashed">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                {activeFiltersCount > 0 
                  ? "Niciun scuter nu corespunde filtrelor selectate"
                  : "Niciun scuter disponibil momentan"
                }
              </p>
              {activeFiltersCount > 0 && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={clearFilters}
                >
                  Șterge filtrele
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Scooter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredScooters.map((scooter) => (
            <ScooterShowcaseCard key={scooter.id} scooter={scooter} defaultImage={defaultImage} />
          ))}
        </div>

        {/* Contact Form Section */}
        <div className="mt-16" id="contact">
          <Separator className="mb-8" />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Info Card */}
            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  Contact Direct
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground">
                  Cel mai rapid mod de a ne contacta este prin WhatsApp. Răspundem în câteva minute!
                </p>
                
                <Button
                  className="w-full bg-green-500 hover:bg-green-600 text-white text-lg py-6"
                  onClick={() => window.open(generateWhatsAppLink(), '_blank')}
                  data-testid="btn-whatsapp-contact"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Contactează pe WhatsApp
                </Button>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-bold text-sm">Alte metode de contact:</h4>
                  
                  <a 
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                  >
                    <Phone className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-mono font-bold">{CONTACT_INFO.phone}</p>
                      <p className="text-xs text-muted-foreground">Apelează direct</p>
                    </div>
                  </a>
                  
                  <a 
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                  >
                    <Mail className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-bold">{CONTACT_INFO.email}</p>
                      <p className="text-xs text-muted-foreground">Trimite email</p>
                    </div>
                  </a>
                  
                  <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                    <MapPin className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-bold">{CONTACT_INFO.location}</p>
                      <p className="text-xs text-muted-foreground">Vizionare cu programare</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Form Card */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-primary" />
                  Formular de Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                {formSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="font-bold text-lg mb-2">Mesaj trimis!</h3>
                    <p className="text-muted-foreground mb-4">
                      Vă vom răspunde cât mai curând posibil.
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setFormSubmitted(false);
                        setFormData({ name: "", email: "", phone: "", message: "", scooterInterest: "" });
                      }}
                    >
                      Trimite alt mesaj
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nume *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Numele dvs."
                          value={formData.name}
                          onChange={handleFormChange}
                          required
                          data-testid="input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefon *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="07XX XXX XXX"
                          value={formData.phone}
                          onChange={handleFormChange}
                          required
                          data-testid="input-phone"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="email@exemplu.com"
                        value={formData.email}
                        onChange={handleFormChange}
                        data-testid="input-email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="scooterInterest">Scuter de interes</Label>
                      <select
                        id="scooterInterest"
                        name="scooterInterest"
                        value={formData.scooterInterest}
                        onChange={handleFormChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="select-scooter"
                      >
                        <option value="">Selectează un scuter (opțional)</option>
                        {filteredScooters.map((s) => (
                          <option key={s.scooter_id} value={`${s.brand} ${s.model} ${s.year} - ${formatPrice(s.selling_price)}`}>
                            {s.brand} {s.model} {s.year} - {formatPrice(s.selling_price)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Mesaj *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Scrieți mesajul dvs. aici..."
                        value={formData.message}
                        onChange={handleFormChange}
                        required
                        rows={4}
                        data-testid="input-message"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={submitting}
                      data-testid="btn-submit-form"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? "Se trimite..." : "Trimite mesajul"}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground text-center">
                      Mesajul va fi trimis prin WhatsApp pentru răspuns rapid
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <Separator className="mb-6" />
          <p className="text-xs text-muted-foreground font-mono">
            © {new Date().getFullYear()} Scutersh • Scutere & Piese Premium
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Fiecare scuter are un dosar digital complet accesibil prin cod QR
          </p>
        </div>
      </main>
    </div>
  );
}

// Showcase card component with image carousel and WhatsApp button
function ScooterShowcaseCard({ scooter, defaultImage }) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  const photos = scooter.photos?.length > 0 
    ? scooter.photos.map(p => getFileUrl(p))
    : [defaultImage];

  const nextPhoto = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(generateWhatsAppLink(scooter), '_blank');
  };

  const getStatusBadge = (status) => {
    if (status === "in_stock") {
      return <Badge className="bg-green-500 text-black font-bold text-xs">Disponibil</Badge>;
    }
    if (status === "reserved") {
      return <Badge className="bg-yellow-500 text-black font-bold text-xs">Rezervat</Badge>;
    }
    return null;
  };

  const scooterTypeInfo = SCOOTER_TYPES[scooter.scooter_type];
  const damageInfo = DAMAGE_HISTORY[scooter.damage_history];

  return (
    <Card className="group overflow-hidden border-border bg-card hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5">
      <Link to={`/scooter/${scooter.scooter_id}`}>
        {/* Image Carousel */}
        <div className="relative overflow-hidden">
          <OptimizedImage
            src={photos[currentPhotoIndex]}
            alt={`${scooter.brand} ${scooter.model}`}
            fallback={defaultImage}
            className="group-hover:scale-105 transition-transform duration-500"
            aspectRatio="4/3"
          />
          
          {/* Status Badge */}
          <div className="absolute top-3 left-3 z-10">
            {getStatusBadge(scooter.status)}
          </div>
          
          {/* Scooter ID */}
          <div className="absolute top-3 right-3 z-10">
            <Badge variant="outline" className="bg-black/50 text-white border-0 font-mono text-xs">
              {scooter.scooter_id}
            </Badge>
          </div>
          
          {/* Type and Damage badges */}
          <div className="absolute bottom-3 left-3 right-3 flex gap-2 z-10">
            {scooterTypeInfo && (
              <Badge variant="outline" className="bg-black/60 text-white border-0 text-xs">
                {scooterTypeInfo.icon} {scooterTypeInfo.label.split(' ')[0]}
              </Badge>
            )}
            {damageInfo && (
              <Badge variant="outline" className={`border-0 text-xs text-white ${damageInfo.color}`}>
                {damageInfo.icon}
              </Badge>
            )}
          </div>
          
          {/* Photo Navigation */}
          {photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
              <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {photos.map((_, idx) => (
                  <span
                    key={idx}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                      idx === currentPhotoIndex ? "bg-primary" : "bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <CardContent className="p-4">
          <div className="mb-3">
            <h3 className="font-heading font-bold text-lg leading-tight">
              {scooter.brand} {scooter.model}
            </h3>
            <p className="text-muted-foreground text-sm font-mono mt-1">
              {scooter.year} • {scooter.engine_cc}cc • {scooter.color}
            </p>
          </div>
          
          {/* Quick Stats */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
            <span className="flex items-center gap-1">
              <Gauge className="w-3 h-3" />
              <span className="font-mono">{scooter.mileage.toLocaleString()} km</span>
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span className="font-mono">{scooter.year}</span>
            </span>
            <span className="flex items-center gap-1">
              <Fuel className="w-3 h-3" />
              <span className="font-mono">{scooter.engine_cc}cc</span>
            </span>
          </div>

          {/* Price */}
          <div className="flex items-center justify-between pt-3 border-t border-border mb-3">
            <span className="text-xs text-muted-foreground">Preț</span>
            <span className="font-mono font-bold text-primary text-xl">
              {formatPrice(scooter.selling_price)}
            </span>
          </div>
        </CardContent>
      </Link>
      
      {/* WhatsApp Button */}
      <div className="px-4 pb-4">
        <Button
          className="w-full bg-green-500 hover:bg-green-600 text-white"
          onClick={handleWhatsAppClick}
          data-testid={`btn-whatsapp-${scooter.scooter_id}`}
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          Contactează pe WhatsApp
        </Button>
      </div>
    </Card>
  );
}
